<script lang="ts">
import type { InputValidator, NullableError } from '@/types'
import {
  generateValidatorsByInputType,
  validate,
} from '@/utils/input-validator-utils'
import { ValidationException } from '@/validation-exception'
import { type PropType, defineComponent } from 'vue'

type BlurEvent = FocusEvent & { target: { name: string } }

/**
 * A general component with common input functionality to extend more specific input types from.
 *
 * @see https://www.w3.org/WAI/tutorials/forms/
 * @see https://www.w3.org/TR/UNDERSTANDING-WCAG20/minimize-error-identified.html
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/G89.html
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/SCR32
 * @see https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/ARIA19.html
 * @see https://www.w3.org/TR/WCAG20-TECHS/ARIA18 On severe errors displays with a alertdialog
 * @see https://www.w3.org/TR/WCAG20-TECHS/ARIA21
 * @see https://www.w3.org/WAI/tutorials/forms/notifications
 */
export default defineComponent({
  inheritAttrs: false,
  props: {
    /**
     * The data to send the form data to.
     */
    data: {
      type: Object as PropType<Record<string, any>>,
      required: true,
    },
    /**
     * An array of objects with the input name, event on when to call the validator,
     * and either a callback or array of callbacks to validate the input.
     */
    validators: {
      type: Object as PropType<Record<
        string,
        InputValidator | Array<InputValidator>
      > | null>,
    },
    errors: {
      type: Object as PropType<NullableError>,
    },
    /**
     * Enable field validation on blur events
     */
    validateOnInput: {
      type: Boolean,
      default: false,
    },
    /**
     * Show footnote about required fields
     */
    showRequiredNote: {
      type: Boolean,
      default: true,
    },
    /**
     * Show required note before buttons
     */
    requiredNoteBeforeButtons: {
      type: Boolean,
      default: false,
    },
    /**
     * Show footnote with complete error messages
     */
    showCompleteErrors: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['submit', 'validation-changed'],

  data() {
    return {
      formErrors: null as NullableError,
      isSubmitting: false,
      formValid: false,
      fieldValidators: {} as Record<
        string,
        InputValidator | Array<InputValidator>
      >,
      // Guard against recursion
      isValidating: false,
      // Track which fields have been touched by the user
      touchedFields: new Set<string>(),
    }
  },

  computed: {
    completeErrors() {
      return this.errors ?? this.formErrors
    },
    errorMessage() {
      const errors = this.completeErrors
      if (typeof errors === 'string') {
        return errors
      }
      if (typeof errors?.message === 'string') {
        return errors.message
      }
      return null
    },
  },

  mounted() {
    this.initializeValidators()
  },

  methods: {
    /**
     * Initialize validators based on form inputs
     */
    initializeValidators() {
      const formRef = this.$refs.formRef
      if (!formRef || !(formRef instanceof HTMLFormElement)) {
        return
      }

      // Get all input elements with proper casting
      const inputs = Array.from<InputElement>(
        formRef.querySelectorAll(
          'input, select, textarea, input[type="checkbox"]'
        )
      )

      // Get provided validators (if any)
      const providedValidators = this.validators ?? {}

      // Generate validators directly, passing the provided validators
      // This way the function will merge them internally
      this.fieldValidators =
        generateValidatorsByInputType(inputs, providedValidators) ?? {}

      // Initial validation for pre-filled forms (typically for logged-in users)
      // But DON'T show errors initially - just track form validity state
      if (this.validateOnInput && Object.values(this.data).some((v) => v)) {
        // Validate without showing errors
        this.validateForm(true)
      }
    },

    /**
     * Validate the entire form
     * @param silently - If true, won't update formErrors
     */
    validateForm(silently: boolean = false): void {
      // Critical: prevent recursion
      if (this.isValidating) {
        return
      }

      this.isValidating = true

      try {
        // Make sure fieldValidators is initialized
        if (
          !this.fieldValidators ||
          Object.keys(this.fieldValidators).length === 0
        ) {
          // Try to initialize validators if not already done
          this.initializeValidators()

          // If still no validators, form is valid by default
          if (Object.keys(this.fieldValidators).length === 0) {
            this.formValid = true
            this.$emit('validation-changed', true)
            return
          }
        }

        const result = validate(this.data, this.fieldValidators)
        const isValid = result === null

        if (this.formValid !== isValid) {
          this.formValid = isValid
          this.$emit('validation-changed', isValid)
        }

        // Set formErrors directly from validation result, unless silent
        if (!silently) {
          this.formErrors = result
        }
      } finally {
        // Always release the lock
        this.isValidating = false
      }
    },

    /**
     * Submit the form and validate
     */
    submitForm() {
      this.isSubmitting = true
      this.formErrors = null

      try {
        // Always refresh validators before submission to catch nested components
        const formRef = this.$refs.formRef
        if (formRef && formRef instanceof HTMLFormElement) {
          this.initializeValidators()
        }

        // Validate with whatever validators we have
        this.formErrors = validate(this.data, this.fieldValidators)

        // Mark touched fields if we have validation errors
        if (this.formErrors && this.formErrors instanceof ValidationException) {
          // Add all fields with errors to touchedFields
          const errorFields = this.formErrors.errors?.value || {}
          for (const fieldName in errorFields) {
            this.touchedFields.add(fieldName)
          }

          // Also mark all validator fields as touched
          for (const fieldName in this.fieldValidators) {
            this.touchedFields.add(fieldName)
          }
        }
      } catch (e: any) {
        this.formErrors = e
      } finally {
        this.isSubmitting = false
      }

      this.$emit('submit', this.formErrors == null)
    },

    /**
     * Clear error for a specific field
     */
    clearError(event: FocusEvent) {
      const blurEvent = event as unknown as BlurEvent

      if (
        blurEvent.target &&
        blurEvent.target.name &&
        this.formErrors instanceof ValidationException
      ) {
        this.formErrors.clear(blurEvent.target.name)
      } else {
        this.formErrors = null
      }
    },

    /**
     * Handle input blur events
     */
    handleBlur(event: FocusEvent) {
      // If validateOnInput is false, just clear the error (original behavior)
      if (!this.validateOnInput) {
        this.clearError(event)
        return
      }

      // Get input element that triggered the blur
      const input = event.target as HTMLInputElement
      if (!input || !input.name) {
        return
      }

      // Get the field name
      const fieldName = input.name

      // Remember that user interacted with this field
      this.touchedFields.add(fieldName)

      // Skip if no validators for this field
      if (!this.fieldValidators || !this.fieldValidators[fieldName]) {
        return
      }

      // Validate the field that just lost focus
      const fieldValidator = {
        [fieldName]: this.fieldValidators[fieldName],
      } as Record<string, InputValidator>
      const validationResult = validate(this.data, fieldValidator)

      // If valid (no errors), remove any existing error for this field
      if (validationResult === null) {
        // Use the existing clearError method to clear errors for this field
        if (
          this.formErrors &&
          this.formErrors instanceof ValidationException &&
          this.formErrors.has(fieldName)
        ) {
          this.clearError(event)
        }

        // Check if the entire form is now valid by validating all fields
        // This is necessary for payment components like Apple Pay to enable
        this.validateForm(true)
      }
      // If invalid, add/update the error
      else {
        // Form is definitely invalid
        if (this.formValid) {
          this.formValid = false
          this.$emit('validation-changed', false)
        }

        // Get error messages for this field
        const fieldErrors = validationResult.get(fieldName)

        // Create or update error object
        if (!this.formErrors) {
          this.formErrors = new ValidationException({
            message: '',
            errors: { [fieldName]: fieldErrors },
          })
        } else if (this.formErrors instanceof ValidationException) {
          const errors = { ...(this.formErrors.errors?.value || {}) }
          errors[fieldName] = fieldErrors
          this.formErrors = new ValidationException({
            message: this.formErrors.message,
            errors,
          })
        }
      }
    },
  },
})
</script>

<template>
  <form
    v-bind="$attrs"
    ref="formRef"
    novalidate
    @submit.stop.prevent="submitForm"
    @blur.capture="handleBlur"
  >
    <p
      v-if="showRequiredNote && requiredNoteBeforeButtons"
      class="mb-2 text-sm"
    >
      * Required fields
    </p>

    <slot :errors="completeErrors" :form-valid="formValid" />

    <p
      v-if="errorMessage && showCompleteErrors"
      class="form-error my-1 text-center text-xs text-red-500"
      v-html="errorMessage"
    />

    <slot name="buttons" :is-submitting :form-valid="formValid" />

    <p v-if="showRequiredNote && !requiredNoteBeforeButtons" class="text-sm">
      * Required fields
    </p>
  </form>
</template>

<style lang="scss">
.form-error {
  a {
    @apply font-bold;
  }
}
</style>
